import {
  DataTable,
  Button,
  Toastify,
  SpinnerLoader,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  TrashAlt,
  BellPlus,
  Pause,
  Pencil,
  Power,
  PowerOff,
  Eye,
} from 'lift-ui';
import { Copy } from 'lucide-react';
import React from 'react';

import { Alert, PaginationData, AlertFilters } from '../types';
import { formatDateTime } from '../utils/helpers';

interface AlertListProps {
  alerts: Alert[];
  loading: boolean;
  pagination: PaginationData;
  onPageChange: (_page: number) => void;
  onPageSizeChange: (_size: number) => void;
  onEditAlert: (_alert: Alert) => void;
  onDuplicateAlert?: (_alert: Alert) => void;
  onDeleteAlert?: (_alertId: number, _userId: number) => void;
  onActivateAlert?: (_alertId: number, _userId: number) => Promise<boolean>;
  onDeactivateAlert?: (_alertId: number, _userId: number) => Promise<boolean>;
  onSendTestAlert: (_alertId: string) => void;
  onFiltersChange: (_filters: AlertFilters) => void;
  filters: AlertFilters;
  alertType: 'my-alerts' | 'global-alerts' | 'admin';
  currentUserId?: number;
  onSubscribeToGlobalAlert?: (_alertId: number) => Promise<boolean>;
  onUnsubscribeFromGlobalAlert?: (_alertId: number) => Promise<boolean>;
  onViewAlert?: (_alert: Alert) => void;
}

export const AlertList: React.FC<AlertListProps> = ({
  alerts,
  loading,
  pagination,
  onPageChange,
  onPageSizeChange,
  onEditAlert: _onEditAlert,
  onDuplicateAlert: _onDuplicateAlert,
  onDeleteAlert: _onDeleteAlert,
  onActivateAlert: _onActivateAlert,
  onDeactivateAlert: _onDeactivateAlert,
  onSendTestAlert: _onSendTestAlert,
  onFiltersChange: _onFiltersChange,
  filters: _filters,
  alertType,
  currentUserId,
  onSubscribeToGlobalAlert,
  onUnsubscribeFromGlobalAlert: _onUnsubscribeFromGlobalAlert,
  onViewAlert,
}) => {
  const [pendingById, setPendingById] = React.useState<Record<string, boolean>>({});
  const [localSubscribedById, setLocalSubscribedById] = React.useState<Record<string, boolean>>({});
  const [pendingActivateDeactivateById, setPendingActivateDeactivateById] = React.useState<
    Record<string, boolean>
  >({});
  const [unsubscribeDialogOpen, setUnsubscribeDialogOpen] = React.useState(false);
  const [alertToUnsubscribe, setAlertToUnsubscribe] = React.useState<{
    alertId: number;
    targetKey: string;
  } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [alertToDelete, setAlertToDelete] = React.useState<{
    alertId: number;
    userId: number;
    alertName?: string;
    brandName?: string;
    toolName?: string;
  } | null>(null);

  const handleUnsubscribeConfirm = async () => {
    if (!alertToUnsubscribe) return;

    const { alertId, targetKey } = alertToUnsubscribe;
    setPendingById(prev => ({ ...prev, [targetKey]: true }));
    try {
      const success = await _onUnsubscribeFromGlobalAlert?.(alertId);
      if (success) {
        Toastify('success', 'Unsubscribed for 30 days');
        setLocalSubscribedById(prev => ({ ...prev, [targetKey]: false }));
      } else {
        Toastify('error', 'Failed to unsubscribe');
      }
    } catch {
      Toastify('error', 'Failed to unsubscribe');
    } finally {
      setPendingById(prev => ({ ...prev, [targetKey]: false }));
      setUnsubscribeDialogOpen(false);
      setAlertToUnsubscribe(null);
    }
  };

  const handleDeleteConfirm = () => {
    if (!alertToDelete || !_onDeleteAlert) return;
    const { alertId, userId } = alertToDelete;
    _onDeleteAlert(alertId, userId);
    setDeleteDialogOpen(false);
    setAlertToDelete(null);
  };

  const columns = [
    {
      accessor: 'alertMessage' as keyof Alert,
      header: 'ALERT NAME',
      key: 'alertMessage',
      width: '25%',
    },
    {
      accessor: 'creator' as keyof Alert,
      header: 'CREATOR',
      key: 'creator',
      width: '15%',
    },
    {
      accessor: 'toolName' as keyof Alert,
      header: 'TOOL NAME',
      key: 'toolName',
      width: '18%',
    },
    {
      accessor: 'brandName' as keyof Alert,
      header: 'BRAND NAME',
      key: 'brandName',
      width: '15%',
    },
    {
      accessor: 'dateTime' as keyof Alert,
      header: 'DATE & TIME',
      key: 'dateTime',
      width: '15%',
      render: (value: any) => formatDateTime(value),
    },
    ...(alertType === 'global-alerts'
      ? [
          {
            accessor: 'actions' as keyof Alert,
            header: 'ACTIONS',
            key: 'actions',
            width: '8%',
            render: (value: any, row: any) => {
              const record = row ?? value?.row ?? value?.original ?? value;
              const alertId = record?.id != null ? parseInt(record.id, 10) : undefined;
              const targetKey = alertId != null ? String(alertId) : undefined;
              const userId = currentUserId || _filters?.user_id || null;
              let isSubscribed = false;
              const usersArray =
                record?.recipients && Array.isArray(record.recipients.users)
                  ? record.recipients.users
                  : [];
              if (userId && usersArray.length > 0) {
                isSubscribed = usersArray.some(
                  (u: any) => u.user_id === userId && u.subscriber === true
                );
              }
              const hasLocal = targetKey && targetKey in localSubscribedById;
              const effectiveSubscribed = hasLocal ? localSubscribedById[targetKey] : isSubscribed;
              const isTestLabAlert = record?.toolName === 'Test Lab Alert';
              return (
                <div className="flex gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        type="button"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (record && onViewAlert) {
                            onViewAlert(record);
                          }
                        }}
                        className="h-8 px-2"
                        aria-label="View alert details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>View alert details</TooltipContent>
                  </Tooltip>
                  {!isTestLabAlert && (effectiveSubscribed ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          type="button"
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!alertId || !targetKey) return;
                            setAlertToUnsubscribe({ alertId, targetKey });
                            setUnsubscribeDialogOpen(true);
                          }}
                          className="h-8 px-2"
                          aria-label="Unsubscribe for 30 days"
                          disabled={!!(targetKey && pendingById[targetKey!])}
                        >
                          {targetKey && pendingById[targetKey!] ? (
                            <SpinnerLoader size={16} />
                          ) : (
                            <Pause className="h-4 w-4 text-red-600" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Unsubscribe for 30 days</TooltipContent>
                    </Tooltip>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          type="button"
                          onClick={async e => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!alertId) return;
                            setPendingById(prev => ({ ...prev, [targetKey!]: true }));
                            try {
                              const success = await onSubscribeToGlobalAlert?.(alertId);
                              if (success) {
                                Toastify('success', 'Subscribed');
                                setLocalSubscribedById(prev => ({ ...prev, [targetKey!]: true }));
                              } else {
                                Toastify('error', 'Failed to subscribe');
                              }
                            } catch {
                              Toastify('error', 'Failed to subscribe');
                            }
                            setPendingById(prev => ({ ...prev, [targetKey!]: false }));
                          }}
                          className="h-8 px-2"
                          aria-label="Subscribe"
                          disabled={!!(targetKey && pendingById[targetKey!])}
                        >
                          {targetKey && pendingById[targetKey!] ? (
                            <SpinnerLoader size={16} />
                          ) : (
                            <BellPlus className="h-4 w-4 text-green-600" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Subscribe</TooltipContent>
                    </Tooltip>
                  ))}
                </div>
              );
            },
          },
        ]
      : []),
    ...(alertType === 'admin'
      ? [
          {
            accessor: 'actions' as keyof Alert,
            header: 'ACTIONS',
            key: 'actions',
            width: '13%',
            render: (value: any, row: any) => {
              const record = row ?? value?.row ?? value?.original ?? value;
              const alertId = record?.id != null ? parseInt(record.id, 10) : undefined;
              const ownerUserId = record?.ownerUserId as number | undefined;
              const isTestLabAlert = record?.toolName === 'Test Lab Alert';
              return (
                <div className="flex gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        type="button"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (record && onViewAlert) {
                            onViewAlert(record);
                          }
                        }}
                        className="h-8 px-2"
                        aria-label="View alert details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>View alert details</TooltipContent>
                  </Tooltip>
                  {!isTestLabAlert && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          type="button"
                          onClick={() => record && _onEditAlert(record)}
                          className="h-8 px-2"
                          aria-label="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Edit</TooltipContent>
                    </Tooltip>
                  )}
                  {!isTestLabAlert && _onDeleteAlert && alertId && ownerUserId && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          type="button"
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!alertId || !ownerUserId || !_onDeleteAlert) return;
                            setAlertToDelete({
                              alertId,
                              userId: ownerUserId,
                              alertName: record?.name,
                              brandName: record?.brandName,
                              toolName: record?.toolName,
                            });
                            setDeleteDialogOpen(true);
                          }}
                          className="h-8 px-2"
                          aria-label="Delete alert"
                        >
                          <TrashAlt className="h-4 w-4 text-red-600" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Delete alert</TooltipContent>
                    </Tooltip>
                  )}
                </div>
              );
            },
          },
        ]
      : []),
    ...(alertType === 'my-alerts'
      ? [
          {
            accessor: 'actions' as keyof Alert,
            header: 'ACTIONS',
            key: 'actions',
            width: '13%',
            render: (value: any, row: any) => {
              const record = row ?? value?.row ?? value?.original ?? value;
              const alertId = record?.id != null ? parseInt(record.id, 10) : undefined;
              const targetKey = alertId != null ? String(alertId) : undefined;
              const userId = currentUserId || _filters?.user_id || null;
              let isSubscribed = false;
              const usersArray =
                record?.recipients && Array.isArray(record.recipients.users)
                  ? record.recipients.users
                  : [];
              if (userId && usersArray.length > 0) {
                isSubscribed = usersArray.some(
                  (u: any) => u.user_id === userId && u.subscriber === true
                );
              }
              const hasLocal = targetKey && targetKey in localSubscribedById;
              const effectiveSubscribed = hasLocal ? localSubscribedById[targetKey] : isSubscribed;

              const isTestLabAlert = record?.toolName === 'Test Lab Alert';
              return (
                <div className="flex gap-2">
                  {/* Subscribe/Unsubscribe bell */}
                  {!isTestLabAlert && (effectiveSubscribed ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          type="button"
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!alertId || !targetKey) return;
                            setAlertToUnsubscribe({ alertId, targetKey });
                            setUnsubscribeDialogOpen(true);
                          }}
                          className="h-8 px-2"
                          aria-label="Unsubscribe for 30 days"
                          disabled={!!(targetKey && pendingById[targetKey!])}
                        >
                          {targetKey && pendingById[targetKey!] ? (
                            <SpinnerLoader size={16} />
                          ) : (
                            <Pause className="h-4 w-4 text-red-600" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Unsubscribe for 30 days</TooltipContent>
                    </Tooltip>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          type="button"
                          onClick={async e => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!alertId) return;
                            setPendingById(prev => ({ ...prev, [targetKey!]: true }));
                            try {
                              const success = await onSubscribeToGlobalAlert?.(alertId);
                              if (success) {
                                Toastify('success', 'Subscribed');
                                setLocalSubscribedById(prev => ({ ...prev, [targetKey!]: true }));
                              } else {
                                Toastify('error', 'Failed to subscribe');
                              }
                            } catch {
                              Toastify('error', 'Failed to subscribe');
                            }
                            setPendingById(prev => ({ ...prev, [targetKey!]: false }));
                          }}
                          className="h-8 px-2"
                          aria-label="Subscribe"
                          disabled={!!(targetKey && pendingById[targetKey!])}
                        >
                          {targetKey && pendingById[targetKey!] ? (
                            <SpinnerLoader size={16} />
                          ) : (
                            <BellPlus className="h-4 w-4 text-green-600" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Subscribe</TooltipContent>
                    </Tooltip>
                  ))}
                  {/* Edit */}
                  {!isTestLabAlert && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        type="button"
                        onClick={() => record && _onEditAlert(record)}
                        className="h-8 px-2"
                        aria-label="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Edit</TooltipContent>
                  </Tooltip>
                  )}
                  {/* Duplicate */}
                  {!isTestLabAlert && _onDuplicateAlert && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          type="button"
                          onClick={() => record && _onDuplicateAlert(record)}
                          className="h-8 px-2"
                          aria-label="Duplicate alert"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Duplicate</TooltipContent>
                    </Tooltip>
                  )}
                  {/* Delete (only for my alerts) */}
                  {!isTestLabAlert && _onDeleteAlert && alertId && userId && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          type="button"
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!alertId || !userId || !_onDeleteAlert) return;
                            setAlertToDelete({
                              alertId,
                              userId,
                              alertName: record?.name,
                              brandName: record?.brandName,
                              toolName: record?.toolName,
                            });
                            setDeleteDialogOpen(true);
                          }}
                          className="h-8 px-2"
                          aria-label="Delete alert"
                        >
                          <TrashAlt className="h-4 w-4 text-red-600" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Delete alert</TooltipContent>
                    </Tooltip>
                  )}
                </div>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <TooltipProvider>
      <div className="flex flex-col h-full">
        {/* Data Table */}
        <div className="flex-1 min-h-0">
          <DataTable
            className="flex-1 min-h-0 overflow-auto"
            maxHeight="calc(100vh - 200px)"
            columns={columns}
            data={alerts}
            onSort={() => {}}
            pagination={{
              currentPage: pagination.currentPage,
              onPageChange: onPageChange,
              onPageSizeChange: onPageSizeChange,
              pageSize: pagination.pageSize,
              pageSizeOptions: [15, 25, 50, 100],
              totalItems: pagination.totalItems,
              totalPages: pagination.totalPages,
            }}
            stickyFooter
            stickyHeader
            loading={loading}
            emptyMessage="No alerts found."
          />
        </div>
      </div>

      {/* Unsubscribe Confirmation Dialog */}
      <Dialog open={unsubscribeDialogOpen} onOpenChange={setUnsubscribeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unsubscribe from Alert</DialogTitle>
            <DialogDescription>
              Are you sure you want to unsubscribe from this alert for 30 days? You will not receive
              any notifications during this period.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setUnsubscribeDialogOpen(false);
                setAlertToUnsubscribe(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleUnsubscribeConfirm}
              disabled={
                !!(alertToUnsubscribe?.targetKey && pendingById[alertToUnsubscribe.targetKey])
              }
            >
              {alertToUnsubscribe?.targetKey && pendingById[alertToUnsubscribe.targetKey] ? (
                <>
                  <SpinnerLoader size={16} className="mr-2" />
                  Unsubscribing...
                </>
              ) : (
                'Unsubscribe for 30 days'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Alert Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={open => {
          if (!open) {
            setDeleteDialogOpen(false);
            setAlertToDelete(null);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete alert?</DialogTitle>
            <DialogDescription>
              <span className="block">Are you sure you want to delete this alert?</span>
            </DialogDescription>
          </DialogHeader>
          {alertToDelete && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 space-y-1">
              {alertToDelete.alertName && (
                <p>
                  <span className="font-semibold">Alert:</span> {alertToDelete.alertName}
                </p>
              )}
              {alertToDelete.brandName && (
                <p>
                  <span className="font-semibold">Brand:</span> {alertToDelete.brandName}
                </p>
              )}
              {alertToDelete.toolName && (
                <p>
                  <span className="font-semibold">Type:</span> {alertToDelete.toolName}
                </p>
              )}
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setDeleteDialogOpen(false);
                setAlertToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Delete alert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};
